import React from "react";
import PropTypes from "prop-types";
import "./productTag.scss";

/**
 * ProductTag Component
 *
 * A card component that displays product information with title, description, and multiple images and names
 *
 * @param {Object} props - Component props
 * @param {string} props.title - The title of the product
 * @param {string} props.description - The description or subtitle of the product
 * @param {Array|string|Object} props.imageSrc - The imported image(s) or source URL(s) for the product image(s)
 * @param {Array|string} props.imageName - The name(s) of the product image(s)
 * @param {string} [props.className] - Additional CSS class for the component
 * @returns {JSX.Element} ProductTag component
 */
const ProductTag = ({ title, description, imageSrc, imageName, className }) => {
  // Convert imageSrc and imageName to arrays if they're not already
  const imageSources = Array.isArray(imageSrc) ? imageSrc : [imageSrc];
  const imageNames = Array.isArray(imageName) ? imageName : [imageName];

  // Determine alignment based on number of images
  const contentAlignment = imageSources.length > 1 ? "flex-start" : "center";

  return (
    <div className={`productTagContainer ${className || ""}`}>
      <div className="productTagInner">
        <div
          className="productTagContent"
          style={{
            alignSelf: contentAlignment
          }}
        >
          <h3 className="productTagTitle">{title}</h3>
          <p className="productTagDescription">{description}</p>
        </div>
        <div className="productTagImageContainer">
          {imageSources.map((src, index) => (
            <div key={index} className="productTagImageItem">
              {src && (
                <div className="productTagImageWrapper">
                  <img
                    src={src}
                    alt={imageNames[index] || title}
                    className="productTagImage"
                  />
                </div>
              )}
              {imageNames[index] && (
                <span className="productTagImageName">{imageNames[index]}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

ProductTag.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  imageSrc: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array
  ]).isRequired,
  imageName: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
    .isRequired,
  className: PropTypes.string
};

export default ProductTag;
